
import { format } from "date-fns";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, AlertTriangle, Info } from "lucide-react";
import { ResizableSwappableCard } from "./ResizableSwappableCard";

interface RecentError {
  id: string;
  level: string;
  message: string;
  location: string;
  created_at: string;
}

interface RecentErrorsTableProps {
  recentErrors: RecentError[];
  rearrangingEnabled: boolean;
  onRemove?: (id: string) => void;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}

export function RecentErrorsTable({ 
  recentErrors, 
  rearrangingEnabled, 
  onRemove,
  size = 'large',
  onResize
}: RecentErrorsTableProps) {
  const getLogLevelBadge = (level) => {
    switch (level?.toLowerCase()) {
      case 'error':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" /> {level}</Badge>;
      case 'warning':
        return <Badge variant="warning"><AlertTriangle className="h-3 w-3 mr-1" /> {level}</Badge>;
      case 'info':
        return <Badge variant="secondary"><Info className="h-3 w-3 mr-1" /> {level}</Badge>;
      case 'success':
        return <Badge variant="success"><CheckCircle className="h-3 w-3 mr-1" /> {level}</Badge>;
      default:
        return <Badge>{level}</Badge>;
    }
  };

  return (
    <ResizableSwappableCard 
      id="recent-errors" 
      size={size}
      showHandle={rearrangingEnabled} 
      showResizeControl={rearrangingEnabled}
      onRemove={rearrangingEnabled ? onRemove : undefined}
      onResize={onResize ? (newSize) => onResize("recent-errors", newSize) : undefined}
    >
      <CardHeader>
        <CardTitle>Recent System Errors</CardTitle>
        <CardDescription>Last 5 errors logged</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Level</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>Time</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {recentErrors.length > 0 ? (
              recentErrors.map((error) => (
                <TableRow key={error.id}>
                  <TableCell>{getLogLevelBadge(error.level)}</TableCell>
                  <TableCell className="font-medium truncate max-w-[200px]">
                    {error.message}
                  </TableCell>
                  <TableCell>
                    {format(new Date(error.created_at), 'MMM dd, HH:mm')}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="text-center">
                  No recent errors
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </ResizableSwappableCard>
  );
}
